import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text, UnstyledButton } from "@mantine/core";
import { IconRocket } from "@tabler/icons";

import ReleaseAssignmentMenu from "./ReleaseAssignmentMenu";
import ReleaseChips from "./ReleaseChips";
import { useReleases } from "../../hooks/useReleases";
import {
  RELEASE_DELIVERY_ITEM_SUFFIX,
} from "../../constants/releases";
import { buildReleaseOptions } from "../../utils/releaseOptions";
import {
  deliveryOnlyReleaseIds,
  releaseIdsFromKey,
  releaseIdsKey,
  resolveLinkedProductItemReleaseIds,
  unionReleaseIds,
} from "../../utils/ideaReleases";

interface IdeaReleaseFieldProps {
  idea: any;
  readOnly?: boolean;
}

/**
 * The Release attribute on the idea detail view, gated by
 * `displayState?.release` at both call sites.
 *
 * There is no field→editor registry on the idea page — every attribute is a
 * hand-written block — so this exists to keep the two hand-written blocks
 * (`ideaNiv/idea-module/IdeaAttributes` and `pages/AiHeader/IdeaAttributes`)
 * from drifting apart. It follows the product-area block's shape: a small
 * labelled header over a pill that opens a menu.
 *
 * **Two sets, deliberately not merged.** The pill edits the idea's own
 * `releaseIds` through `PUT /v1/ideas/:id/releases`, and needs no delivery
 * link to do it. Below it, when the linked delivery item carries releases of
 * its own, they are shown read-only: they belong to that item, two ideas can
 * link to it, and editing them from here would move them for everyone.
 * `idea.releaseIds` is optional and absent on most ideas, so it is never
 * assumed to be an array.
 */
export const IdeaReleaseField: React.FC<IdeaReleaseFieldProps> = ({
  idea,
  readOnly = false,
}) => {
  const { releases, groups } = useReleases();

  const options = useMemo(
    () => buildReleaseOptions(releases, groups),
    [releases, groups]
  );

  const ideaId = idea?._id ? String(idea._id) : undefined;

  const linkedReleaseIds = useMemo(
    () => resolveLinkedProductItemReleaseIds(idea),
    [idea]
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const allReleaseIds = useMemo(
    () => unionReleaseIds(selectedIds, linkedReleaseIds),
    [selectedIds, linkedReleaseIds]
  );
  const deliveryOnlyIds = useMemo(
    () => deliveryOnlyReleaseIds(selectedIds, linkedReleaseIds),
    [selectedIds, linkedReleaseIds]
  );

  // Keyed on the ids, not the array: `onSaved` has already moved the chips to what the
  // server accepted, and a parent re-render must not revert it.
  const ideaReleaseKey = releaseIdsKey(idea?.releaseIds);
  useEffect(() => {
    setSelectedIds(releaseIdsFromKey(ideaReleaseKey));
  }, [ideaReleaseKey, idea?._id]);

  return (
    <Box data-testid="idea-release-field">
      <Flex ml={"4px"} align="center" mb={"4px"}>
        <IconRocket size={14} color="#585C68" />
        <Text
          fz={"12px"}
          color={"#585C68"}
          ml={"6px"}
          miw={"85px"}
          sx={{ lineHeight: "16px" }}
        >
          Release
        </Text>
      </Flex>
      {/* One box, not two. The idea's own releases and its delivery item's render as a single
          chip strip so `+N` and the tooltip count the whole set; the delivery item's are muted
          per chip and named in the tooltip, because only the idea's own are editable here. */}
      <ReleaseAssignmentMenu
        ideaId={ideaId}
        releaseIds={selectedIds}
        readOnly={readOnly}
        onSaved={setSelectedIds}
      >
        <UnstyledButton>
          <Flex
            h={24}
            px={8}
            py={4}
            mx={"2px"}
            bg="#5C5CEB1A"
            justify="space-evenly"
            align="center"
            data-testid="idea-release-chips"
            sx={{
              borderRadius: "4px",
              border: "0.8px solid #F8e9FB",
              cursor: ideaId && !readOnly ? "pointer" : "default",
            }}
          >
            <ReleaseChips
              releaseIds={allReleaseIds}
              options={options}
              dimmedIds={deliveryOnlyIds}
              dimmedLabelSuffix={RELEASE_DELIVERY_ITEM_SUFFIX}
            />
          </Flex>
        </UnstyledButton>
      </ReleaseAssignmentMenu>
    </Box>
  );
};

export default IdeaReleaseField;
